import { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FileText, Trash2, ExternalLink, UploadCloud, Loader2 } from "lucide-react";

const CATS = ["Tender Documents", "Company Policies", "Supplier Agreements", "Certificates", "Compliance Documents", "Quotations", "Invoices", "Purchase Orders", "Delivery Notes"];

export default function Documents() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [expiry, setExpiry] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/documents").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const reset = () => { setTitle(""); setCategory(""); setExpiry(""); setFile(null); };

  const save = async () => {
    if (!title || !file) return toast.error("Title and a file are required");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("category", category);
      fd.append("expiry_date", expiry);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setOpen(false); reset(); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const del = async (d) => {
    if (!(await confirm({ title: "Delete document?", description: `"${d.title}" will be removed from the repository.`, confirmText: "Delete" }))) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Only admins can delete"); }
  };

  const fileHref = (d) => d.storage_path ? `${API}/files/${d.storage_path}` : d.url;
  const shown = filter === "all" ? items : items.filter((d) => d.category === filter);
  const expired = (d) => d.expiry_date && new Date(d.expiry_date) < new Date();

  return (
    <div>
      <PageHeader title="Document Management" subtitle="Secure, version-controlled document repository.">
        <Button onClick={() => setOpen(true)} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="add-document-button"><Plus className="h-4 w-4 mr-1.5" /> Upload Document</Button>
      </PageHeader>

      <div className="mb-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[220px] h-10 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((d) => (
          <div key={d.id} className="bg-white border border-border rounded-md p-4 group" data-testid="document-card">
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><FileText className="h-5 w-5 text-primary" /></div>
              <Button size="icon" variant="ghost" className="h-8 w-8 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity" onClick={() => del(d)} data-testid={`delete-document-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="mt-3 font-semibold leading-tight">{d.title}</div>
            <div className="text-xs text-muted-foreground mt-1">{d.category}{d.original_filename ? ` · ${d.original_filename}` : ""}</div>
            <div className="flex items-center gap-2 mt-3">
              <Badge variant="outline" className="text-[10px]">v{d.version}</Badge>
              {expired(d) && <Badge className="bg-primary text-white text-[10px]">Expired</Badge>}
              {d.expiry_date && !expired(d) && <span className="text-[10px] text-muted-foreground">exp {d.expiry_date}</span>}
              <a href={fileHref(d)} target="_blank" rel="noreferrer" className="ml-auto text-primary text-xs font-medium flex items-center gap-1 hover:underline" data-testid="document-open-link">Open <ExternalLink className="h-3 w-3" /></a>
            </div>
          </div>
        ))}
        {shown.length === 0 && <p className="col-span-full text-center py-12 text-muted-foreground">No documents.</p>}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="document-title-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">Category</Label>
              <Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="document-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">File</Label>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-border rounded-md p-6 text-center cursor-pointer hover:border-primary transition-colors"
                data-testid="document-dropzone"
              >
                <UploadCloud className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                {file ? <span className="text-sm font-medium">{file.name}</span> : <span className="text-sm text-muted-foreground">Click to choose a PDF, image or document (max 25MB)</span>}
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="document-file-input" />
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">Expiry Date (optional)</Label><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={uploading} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="save-document-button">
              {uploading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Uploading</> : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
